import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiEye, FiEdit, FiCalendar, FiUser, FiFolder } from 'react-icons/fi';
import { useAppContext } from '../context/AppContext';
import SectionTitle from '../components/SectionTitle';
import Badge from '../components/Badge';
import PromptModal from '../components/PromptModal';

// Mock data for demonstration
const mockProjects = [
  {
    id: '1',
    projectName: 'E-commerce Platform',
    developerName: 'John Doe',
    dateCreated: '2024-01-15T10:30:00Z',
    projectDraft: JSON.stringify({
      category: 'e-commerce',
      complexity: 'complex',
      database: 'ncb',
      siteTitle: 'Modern Shop',
      features: ['responsiveDesign', 'auth', 'search']
    }),
    finalPrompt: 'Build a comprehensive e-commerce platform with React, TailwindCSS...'
  },
  {
    id: '2',
    projectName: 'Business Website',
    developerName: 'Jane Smith',
    dateCreated: '2024-01-10T14:20:00Z',
    projectDraft: JSON.stringify({
      category: 'small-business',
      complexity: 'simple',
      database: 'supabase',
      siteTitle: 'Smith Consulting',
      features: ['responsiveDesign', 'animations']
    }),
    finalPrompt: 'Create a professional business website with modern design...'
  }
];

const ProjectCard = ({ project }) => {
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const { dispatch } = useAppContext();
  const navigate = useNavigate();
  
  const projectDraft = JSON.parse(project.projectDraft || '{}');
  
  const handleEditProject = () => {
    dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: { ...projectDraft, id: project.id } });
    navigate('/start-building');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      <PromptModal 
        isOpen={isPromptModalOpen} 
        onClose={() => setIsPromptModalOpen(false)} 
        promptText={project.finalPrompt || 'No prompt was generated for this project.'} 
      />
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">{project.projectName || 'Untitled Project'}</h3>
          <FiFolder className="h-5 w-5 text-gray-400" />
        </div>
        
        <div className="space-y-3 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <FiUser className="h-4 w-4 mr-2" />
            {project.developerName || 'Unknown'}
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <FiCalendar className="h-4 w-4 mr-2" />
            {new Date(project.dateCreated).toLocaleDateString()}
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge className="bg-blue-100 text-blue-800">
            {projectDraft.category || 'No Category'}
          </Badge>
          <Badge className="bg-green-100 text-green-800">
            {projectDraft.complexity || 'N/A'}
          </Badge>
          <Badge className="bg-purple-100 text-purple-800">
            {projectDraft.database || 'N/A'}
          </Badge>
        </div>
      </div>
      
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-2">
        <button 
          onClick={() => setIsPromptModalOpen(true)} 
          disabled={!project.finalPrompt}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          <FiEye className="mr-2 h-4 w-4" /> 
          View Prompt
        </button>
        
        <button 
          onClick={handleEditProject}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition-all duration-200"
        >
          <FiEdit className="mr-2 h-4 w-4" /> 
          Edit Project
        </button>
      </div>
    </motion.div>
  );
};

function SavedProjects() {
  const projects = mockProjects; // In real app, fetch from API

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <SectionTitle
        title="Saved Projects"
        subtitle="Review, edit, or view prompts for your saved project drafts."
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.length > 0 ? (
          projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <ProjectCard project={project} />
            </motion.div>
          ))
        ) : (
          <div className="col-span-full text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiFolder className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg">No saved projects yet</p>
            <p className="text-gray-400 text-sm mt-2">Start building your first project to see it here.</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default SavedProjects;